import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const SITE = "https://vendororbis.com";
const DEFAULT_TITLE = "VendorOrbis | POS & Event Management for Market Vendors";
const DEFAULT_DESCRIPTION =
  "The point-of-sale and back office for market, craft fair, and pop-up vendors. Take card and cash payments, track inventory by event, pay staff, and see which events make you money.";

const PAGES: Record<string, { title: string; description: string }> = {
  "/": { title: DEFAULT_TITLE, description: DEFAULT_DESCRIPTION },
  "/pricing": {
    title: "Pricing | VendorOrbis",
    description:
      "Free forever for up to 10 active events and 25 items. Upgrade to Lite or Pro for more, and try Pro free for 30 days. Square is included on every plan.",
  },
  "/faq": {
    title: "FAQ | VendorOrbis",
    description:
      "Answers about selling with VendorOrbis: checkout and offline sales, Square and Tap to Pay, inventory, refunds, staff pay, reports, and plans.",
  },
  "/support": {
    title: "Support | VendorOrbis",
    description: "Get help with VendorOrbis for your markets, events, and sales.",
  },
  "/tools": {
    title: "Vendor Tools | VendorOrbis",
    description: "Free tools for event vendors, including a booth layout planner.",
  },
  "/tools/booth-layout": {
    title: "Booth Layout Planner | VendorOrbis",
    description:
      "Plan your tent, tables, and displays before load-in with a free booth layout planner.",
  },
  "/privacy-policy": {
    title: "Privacy Policy | VendorOrbis",
    description: "How VendorOrbis collects, uses, and protects your data.",
  },
  "/terms-and-condition": {
    title: "Terms and Conditions | VendorOrbis",
    description: "The terms for using VendorOrbis.",
  },
  "/eula": {
    title: "End User License Agreement | VendorOrbis",
    description: "The VendorOrbis end user license agreement.",
  },
};

const setMeta = (selector: string, attr: string, value: string) => {
  const el = document.head.querySelector(selector);
  if (el) el.setAttribute(attr, value);
};

// Keeps title, description, canonical and social tags in sync with the route
// so each public page is indexed with its own snippet. Private pages are
// marked noindex.
const PageMeta = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    const path = pathname.length > 1 ? pathname.replace(/\/$/, "") : pathname;
    const page = PAGES[path];
    const title = page?.title ?? DEFAULT_TITLE;
    const description = page?.description ?? DEFAULT_DESCRIPTION;
    const url = page ? `${SITE}${path === "/" ? "/" : path}` : `${SITE}/`;

    document.title = title;
    setMeta('meta[name="description"]', "content", description);
    setMeta('link[rel="canonical"]', "href", url);
    setMeta('meta[property="og:title"]', "content", title);
    setMeta('meta[property="og:description"]', "content", description);
    setMeta('meta[property="og:url"]', "content", url);
    setMeta('meta[property="twitter:title"]', "content", title);
    setMeta('meta[property="twitter:description"]', "content", description);

    let robots = document.head.querySelector('meta[name="robots"]');
    if (!page) {
      if (!robots) {
        robots = document.createElement("meta");
        robots.setAttribute("name", "robots");
        document.head.appendChild(robots);
      }
      robots.setAttribute("content", "noindex");
    } else if (robots) {
      robots.remove();
    }
  }, [pathname]);

  return null;
};

export default PageMeta;
